import {
  CUSTOMER_SERVICE_PHONE_NUMBER,
  SALES_PHONE_NUMBER,
} from '@seek/shared-constants';
import {
  Text,
  Heading,
  Hidden,
  Stack,
  Box,
  ContentBlock,
  Columns,
  Column,
} from 'braid-design-system';
import React, { type FC } from 'react';

import { TRACKING_CODES } from 'src/shared/constant';
import { useLocale, useLocalized, useSite } from 'src/shared/hooks';
import {
  ACCOUNT_BRANDING,
  SIGN_IN,
  JOB_ADS,
  CONNECTED_PARTNERS,
} from 'src/shared/links';
import type { PageProps, PhoneNumber, Site } from 'src/shared/types';

import {
  StaticPageProvider,
  TextLink,
  ResponsiveDivider,
} from '../../../../components';

import BrandingTools from './components/BrandingTools';
import CTAButtonNoLinks from './components/CTAButtonNoLinks';
import FooterBlock from './components/FooterBlock';
import IconSellingPoints from './components/IconSellingPoints';
import SellingPoints from './components/SellingPoints';
import Testimonials from './components/Testimonials';

import * as styles from './style/style.css';

const StaticPageProps: PageProps = {
  title: 'Branding',
  currentPage: 'branding',
  returnUrl: 'products/branding/',
  eventName: 'branding-page-viewed',
  siteSection: 'products',
  activeTab: 'Products',
  activeSubTab: 'branding',
};

const contentByLocale = {
  NZ: {
    buildYourBrandDescription: `Use our StandOut and Premium ads to build your brand and
                    attract the right candidates. Our optimised ad design
                    ensures the candidates you want to attract will see your
                    brand.`,
    addABrandToJobAdsDescription: `Showcase your brand by including a custom cover image
                        and your logo in your StandOut or Premium ads.`,
    bringYourBrandDescription: () => (
      <>
        Find out more about our{' '}
        <TextLink
          target="_blank"
          href={JOB_ADS}
          tracking={TRACKING_CODES.jobs_ads_link_clicked}
        >
          StandOut and Premium ads
        </TextLink>
        .
      </>
    ),
  },
  AU: {
    buildYourBrandDescription: `Want your job ads to stand out? Branded Basic, Branded Advanced, and Premium Ads help you attract the right candidates. Our optimised ad design ensures the candidates you want to attract will see your brand.`,
    addABrandToJobAdsDescription: `Showcase your brand by including a custom cover image and your logo in your Branded Basic, Branded Advanced, or Premium Ads.`,
    bringYourBrandDescription: () => (
      <>
        Find out more about our{' '}
        <TextLink
          target="_blank"
          href={JOB_ADS}
          tracking={TRACKING_CODES.jobs_ads_link_clicked}
        >
          job ads
        </TextLink>
        .
      </>
    ),
  },
};

const Branding: FC = () => {
  const site: Site = useSite();
  const locale = useLocale();
  const content =
    contentByLocale[locale as keyof typeof contentByLocale] ||
    contentByLocale.AU;
  return (
    <StaticPageProvider {...StaticPageProps}>
      <Box background="surface">
        <Stack space={['xlarge', 'xxlarge']}>
          <Box style={{ backgroundColor: '#F2F7FE' }}>
            <ContentBlock>
              {/* top 100px is set to offset the whole component downwards */}
              <Box
                position="relative"
                paddingX={{ mobile: 'small', desktop: 'none' }}
                className={styles.topBanner}
              >
                <Stack space="xlarge" align="center">
                  <Stack space="large" align="center">
                    <Heading level="1" align="center">
                      Attract candidates with your brand
                    </Heading>
                    <Box style={{ maxWidth: '614px' }}>
                      <Text align="center" size="large">
                        You&apos;ve invested time and money into building your
                        brand. Use our branding capabilities and be seen in{' '}
                        {useLocalized({ NZ: 'New Zealand', AU: 'Australia' })}
                        &apos;s leading employment marketplace.
                      </Text>
                    </Box>
                  </Stack>
                  <Box paddingTop="xlarge" className={styles.image1} />
                </Stack>
              </Box>
            </ContentBlock>
          </Box>
          {/* add paddingTop 100px to cater for the offset from components above */}
          <Box
            className={styles.topBannerOffset}
            paddingX="gutter"
            style={{ textWrap: 'balance' }}
          >
            <ContentBlock>
              <SellingPoints />
            </ContentBlock>
          </Box>
          <ResponsiveDivider />

          <ContentBlock>
            <Box
              paddingX={{ mobile: 'small', desktop: 'none' }}
              style={{ textWrap: 'balance' }}
            >
              <Stack space="large" align="center">
                <Heading level="2">Build your brand, power your hiring</Heading>
                <Box style={{ maxWidth: '612px' }}>
                  <Text align="center" tone="secondary" size="large">
                    {content.buildYourBrandDescription}
                  </Text>
                </Box>
                <IconSellingPoints />
              </Stack>
            </Box>
          </ContentBlock>

          <ResponsiveDivider />

          <ContentBlock>
            <Box
              paddingX={{ mobile: 'small', desktop: 'none' }}
              style={{ textWrap: 'balance' }}
            >
              <Columns
                space={['large', 'large', 'xxlarge']}
                collapseBelow="tablet"
                align="center"
              >
                <Column width="1/2">
                  <Stack space="large">
                    <Heading level="3">
                      Use branding with your recruitment software
                    </Heading>
                    <Box style={{ maxWidth: '417px' }}>
                      <Text size="large" tone="secondary">
                        Create your brand on SEEK using our branding tool.
                        <br />
                        When you&apos;re posting a job using one of our{' '}
                        <TextLink
                          href={CONNECTED_PARTNERS}
                          target="_blank"
                          tracking={TRACKING_CODES.connected_partners_clicked}
                        >
                          Recruitment Software Partners
                        </TextLink>{' '}
                        you&apos;ll be able to select the brands you&apos;ve
                        created.
                        <br />
                        <br />
                        Need help? Reach out to our Customer Service team on{' '}
                        <TextLink
                          href={`tel:${
                            (CUSTOMER_SERVICE_PHONE_NUMBER as PhoneNumber)[site]
                          }`}
                          tracking={
                            TRACKING_CODES.customer_service_phone_number_clicked
                          }
                          skipResolvedUrl
                        >
                          {(CUSTOMER_SERVICE_PHONE_NUMBER as PhoneNumber)[site]}
                        </TextLink>
                        .
                      </Text>
                    </Box>
                  </Stack>
                </Column>
                <Column>
                  <Box className={styles.image4} />
                </Column>
              </Columns>
            </Box>
          </ContentBlock>

          <ResponsiveDivider />

          <ContentBlock>
            <Box
              paddingX={{ mobile: 'small', desktop: 'none' }}
              style={{ textWrap: 'balance' }}
            >
              <Stack space={['xlarge', 'xxlarge']}>
                <Heading level="2" align="center">
                  Managing your brand on SEEK
                </Heading>
                <Columns space="gutter" collapseBelow="tablet" alignY="center">
                  <Column width="1/2">
                    <Box className={styles.image5} />
                  </Column>
                  <Column>
                    <Stack space="large">
                      <Heading level="3">Add a brand to job ads</Heading>
                      <Text size="large" tone="secondary">
                        {content.addABrandToJobAdsDescription}
                      </Text>
                    </Stack>
                  </Column>
                </Columns>
                <Hidden above="mobile">
                  <Columns
                    space="gutter"
                    collapseBelow="tablet"
                    alignY="center"
                  >
                    <Column width="1/2">
                      <Box className={styles.image6} />
                    </Column>
                    <Column>
                      <Stack space="large">
                        <Heading level="3">
                          Create brand combinations easily
                        </Heading>
                        <Text size="large" tone="secondary">
                          Keep multiple logo and cover image combinations for
                          bespoke brand creative.
                        </Text>
                      </Stack>
                    </Column>
                  </Columns>
                </Hidden>
                <Hidden below="tablet">
                  <Columns
                    space="gutter"
                    collapseBelow="tablet"
                    alignY="center"
                  >
                    <Column>
                      <Stack space="large">
                        <Heading level="3">
                          Create brand combinations easily
                        </Heading>
                        <Text size="large" tone="secondary">
                          Keep multiple logo and cover image
                          <br />
                          combinations for bespoke brand creative.
                        </Text>
                      </Stack>
                    </Column>
                    <Column width="1/2">
                      <Box className={styles.image6} />
                    </Column>
                  </Columns>
                </Hidden>

                <Columns space="gutter" collapseBelow="tablet" alignY="center">
                  <Column width="1/2">
                    <Box className={styles.image7} />
                  </Column>
                  <Column>
                    <Stack space="large">
                      <Heading level="3">Update brands anytime</Heading>
                      <Text size="large" tone="secondary">
                        Refresh your brands whenever you need to. Edit your
                        brands using the branding tool. Or update the brand on a
                        job ad anytime.
                      </Text>
                    </Stack>
                  </Column>
                </Columns>
              </Stack>
            </Box>
          </ContentBlock>

          <Box
            style={{ backgroundColor: '#F4F9FF', textWrap: 'balance' }}
            paddingY={['xlarge', 'xxlarge']}
            paddingX={['small', 'small', 'none']}
          >
            <ContentBlock>
              <Stack space="xlarge" align="center">
                <Box style={{ maxWidth: '618px' }}>
                  <Stack space="large" align="center">
                    <Heading level="2">Using our branding tool</Heading>
                    <Text size="large" tone="secondary" align="center">
                      It&apos;s quick and easy to include branding on job ads
                      you post on SEEK or through your recruitment software. The
                      SEEK branding tool offers a range of features for you to
                      create a powerful brand.
                    </Text>
                  </Stack>
                </Box>
                <BrandingTools />
                <Box className={styles.image8} />
                <CTAButtonNoLinks
                  text={{
                    loggedIn: 'Create a brand',
                    loggedOut: 'Create a brand',
                  }}
                  href={{
                    loggedIn: ACCOUNT_BRANDING,
                    loggedOut: SIGN_IN,
                  }}
                  tracking={{
                    loggedIn: 'create_job_link_clicked',
                    loggedOut: 'sign-in-link-clicked',
                  }}
                  tone="formAccent"
                  variant="ghost"
                />
              </Stack>
            </ContentBlock>
          </Box>

          {false ? ( // we hide testimonials temporarily, because there is no real testimony yet from product side
            <ContentBlock>
              <Testimonials />
            </ContentBlock>
          ) : null}
          {false ? ( // hide divider for testimonial
            <ResponsiveDivider />
          ) : null}

          <ContentBlock>
            <Box paddingX={['gutter', 'gutter', 'none']}>
              <Columns space="large" collapseBelow="tablet" alignY="center">
                <Column>
                  <Stack space="large" align="center">
                    <Box className={styles.image9} />
                  </Stack>
                </Column>
                <Column width="1/2">
                  <Stack space="large">
                    <Heading level="2">Digital Media Solutions</Heading>
                    <Text tone="secondary">
                      Showcase your brand on or off SEEK to a highly targeted
                      and relevant audience. Use Display, Video or Native ads to
                      raise awareness and attract talent.
                      <br />
                      <br />
                      For more information on advertising, contact one of our
                      Sales teams on{' '}
                      <TextLink
                        href={`tel:${
                          (SALES_PHONE_NUMBER as PhoneNumber)[site]
                        }`}
                        skipResolvedUrl
                        tracking={TRACKING_CODES.sales_phone_clicked}
                      >
                        {(SALES_PHONE_NUMBER as PhoneNumber)[site]}
                      </TextLink>
                      .
                    </Text>
                  </Stack>
                </Column>
              </Columns>
            </Box>
          </ContentBlock>

          <ContentBlock>
            <Box paddingX={['gutter', 'none']}>
              <Box
                background="neutralLight"
                paddingY="xlarge"
                paddingX={['gutter', 'none']}
                borderRadius="xlarge"
              >
                <Stack space="large" align="center">
                  <Heading level="2" align="center">
                    Bring your brand to SEEK today
                  </Heading>
                  <Text size="large" align="center" tone="neutral">
                    {content.bringYourBrandDescription()}
                    <br />
                    Or reach out to our Customer Service team on{' '}
                    <TextLink
                      href={`tel:${
                        (CUSTOMER_SERVICE_PHONE_NUMBER as PhoneNumber)[site]
                      }`}
                      skipResolvedUrl
                      tracking={
                        TRACKING_CODES.customer_service_phone_number_clicked
                      }
                    >
                      {(CUSTOMER_SERVICE_PHONE_NUMBER as PhoneNumber)[site]}
                    </TextLink>
                    .
                  </Text>
                </Stack>
              </Box>
            </Box>
          </ContentBlock>

          <FooterBlock />
        </Stack>
      </Box>
    </StaticPageProvider>
  );
};

export default Branding;
